import React, { useEffect, useState, useId } from 'react';
import * as PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
    Button,
    Row,
    Col,
    CheckboxControl,
    RadioControl,
    FieldsetControl,
} from '@si-armes/dsfr-react';
import { useForm, FormProvider } from 'react-hook-form';
import InputFiles from 'components/Commun/Form/InputFiles';
import { etapePrecedente } from 'store/etapes/etapesActions';
import { getArrayFilesToSend } from 'utils/fileFunction';
import InfoChampsObligatoire from '../../../../../../components/form/infoObligatoire/InfoChampsObligatoire';

const AutorisationDetentionArmeForm = ({
    defaultValues,
    loading,
    enregistrer,
    isMineurNonEmancipe,
}) => {
    const dispatch = useDispatch();
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues,
    });
    const [informationCompetiteur, setInformationCompetiteur] = useState(undefined);
    const [suiviTraitementPsychiatrique, setSuiviTraitementPsychiatrique] = useState(undefined);
    const { unregister, register, handleSubmit, formState, getValues } = hookForm;
    const unregisterForm = () => {
        if (suiviTraitementPsychiatrique === true) {
            unregister('jamaisEteEnTraitementPsychiatrique');
        } else {
            unregister('pieceJointes.certificatMedicalPsychiatrique.fichiers');
        }
    };

    const onSubmit = async (dataForm) => {
        const pieceJointes = await Promise.all(
            Object.entries(dataForm.pieceJointes)
                .filter(([, pieceJointe]) => pieceJointe.fichiers)
                .map(async ([type, pieceJointe]) => {
                    const fichiers = await getArrayFilesToSend(pieceJointe.fichiers);
                    return {
                        type,
                        fichiers,
                    };
                }),
        );
        const { informationCompetiteur: informationsCompetiteur, ...dataToSend } = dataForm;

        enregistrer({
            ...dataToSend,
            pieceJointes,
        });
    };

    useEffect(() => {
        if (suiviTraitementPsychiatrique === false) {
            unregister('pieceJointes.certificatMedicalPsychiatrique.fichiers');
        }
    }, [suiviTraitementPsychiatrique, unregister]);

    return (
        <>
            <FormProvider {...hookForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Row gutters>
                        <Col lg={12}>
                            <h3 className="margin-top-xs">
                                Récapitulatif des informations pour la nouvelle demande
                                d'autorisation
                            </h3>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Type de demande : </strong>{' '}
                                {getValues('primoDemandeur') === true
                                    ? 'Première demande'
                                    : 'Renouvellement'}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Motif : </strong> {getValues('motif')}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Avis favorable de la Fédération Française de Tir : </strong>{' '}
                                {getValues('avisFavorableFFTir') === true ? 'Oui' : 'Non'}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Licence de tir en cours de validité : </strong>{' '}
                                {getValues('licenceTirValide') === true ? 'Oui' : 'Non'}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Licence de ball-trap en cours de validité : </strong>{' '}
                                {getValues('licenceBallTrapValide') === true ? 'Oui' : 'Non'}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <span>
                                <strong>Licence de ski en cours de validité : </strong>{' '}
                                {getValues('licenceSkiValide') === true ? 'Oui' : 'Non'}
                            </span>
                        </Col>
                    </Row>
                    <Row gutters className="margin-top-md">
                        <Col lg={12}>
                            <h3 className="margin-top-xs">
                                Informations supplémentaires à renseigner
                            </h3>
                        </Col>
                    </Row>
                    <Row>
                        <Col col={12}>
                            <InfoChampsObligatoire />
                        </Col>
                    </Row>
                    <Row gutters>
                        <Col lg={12}>
                            <div className="margin-bottom-md">
                                <InputFiles
                                    label={
                                        isMineurNonEmancipe
                                            ? 'Acte de naissance du responsable légal'
                                            : 'Acte de naissance'
                                    }
                                    {...register(
                                        isMineurNonEmancipe
                                            ? 'pieceJointes.acteNaissanceResponsableLegal.fichiers'
                                            : 'pieceJointes.acteNaissance.fichiers',
                                        {
                                            required: 'Ce champ est obligatoire',
                                        },
                                    )}
                                    error={
                                        isMineurNonEmancipe
                                            ? formState.errors?.pieceJointes
                                                  ?.acteNaissanceResponsableLegal
                                            : formState.errors?.pieceJointes?.acteNaissance
                                    }
                                    required
                                />
                                <span>
                                    Veuillez importer un extrait d'acte de naissance avec mentions
                                    marginales de moins de 3 mois
                                </span>
                            </div>
                            <br />
                            <div className="margin-bottom-md">
                                <InputFiles
                                    label={
                                        isMineurNonEmancipe
                                            ? 'Avis préalable délivré par la Fédération Française de tir du détenteur mineur'
                                            : 'Avis préalable délivré par la Fédération Française de Tir'
                                    }
                                    {...register('pieceJointes.avisPrealableFftir.fichiers', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                    error={
                                        formState.errors
                                            ? formState.errors?.pieceJointes?.avisPrealableFftir
                                            : null
                                    }
                                    required
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutters>
                        <Col lg={12}>
                            <div className="margin-bottom-sm">
                                <label
                                    htmlFor="installationConservationArmesCategoriesAEtB"
                                    className="fr-label fr-pb-2w"
                                >
                                    {isMineurNonEmancipe
                                        ? "Je déclare sur l'honneur que le détenteur mineur dispose des installations nécessaires à la conservation d'armes à feu de catégories A et B *"
                                        : "Je déclare sur l'honneur disposer des installations nécessaires à la conservation d'armes à feu de catégories A et B *"}
                                    <span className="fr-hint-text">
                                        Les armes à feu, leurs éléments et leurs munitions de
                                        catégories A et B doivent être conservés soit dans des
                                        coffres forts ou des armoires fortes adaptés au type et au
                                        nombre de matériels détenus; Soit à l’intérieur de pièces
                                        fortes comportant une porte blindée et dont les ouvrants
                                        sont protégés par des barreaux
                                    </span>
                                </label>
                                <CheckboxControl
                                    id="installationConservationArmesCategoriesAEtB"
                                    name="installationConservationArmesCategoriesAEtB"
                                    {...register('installationConservationArmesCategoriesAEtB', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                    state={
                                        formState.errors
                                            ?.installationConservationArmesCategoriesAEtB
                                            ? 'error'
                                            : null
                                    }
                                    message={
                                        formState.errors
                                            ?.installationConservationArmesCategoriesAEtB?.message
                                    }
                                    required
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row gutters className="margin-top-sm">
                        <Col lg={12}>
                            <FieldsetControl
                                id="suiviTraitementPsychiatrique"
                                horizontalAlign
                                legend={
                                    isMineurNonEmancipe
                                        ? 'Le détenteur mineur a-t-il déjà suivi un traitement dans un hôpital psychiatrique ? *'
                                        : 'Avez-vous déjà suivi un traitement dans un hôpital psychiatrique ? *'
                                }
                                state={
                                    formState.errors?.suiviTraitementPsychiatrique ? 'error' : null
                                }
                                message={formState.errors?.suiviTraitementPsychiatrique?.message}
                                inline
                            >
                                <RadioControl
                                    className="margin-right-sm"
                                    id={useId()}
                                    {...register('suiviTraitementPsychiatrique', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                    label="Non"
                                    value={false}
                                    onClick={() => setSuiviTraitementPsychiatrique(false)}
                                />
                                <RadioControl
                                    className="margin-right-sm"
                                    id={useId()}
                                    {...register('suiviTraitementPsychiatrique')}
                                    label="Oui"
                                    value
                                    onClick={() => setSuiviTraitementPsychiatrique(true)}
                                />
                            </FieldsetControl>
                        </Col>
                    </Row>
                    {suiviTraitementPsychiatrique === true && (
                        <Row gutters>
                            <Col lg={12}>
                                <InputFiles
                                    label="Certificat médical (médecin psychiatre)"
                                    {...register(
                                        'pieceJointes.certificatMedicalPsychiatrique.fichiers',
                                        { required: 'Ce champ est obligatoire' },
                                    )}
                                    error={
                                        formState.errors
                                            ? formState.errors?.pieceJointes
                                                  ?.certificatMedicalPsychiatrique?.fichiers
                                            : null
                                    }
                                />
                                <span>
                                    Veuillez importer un certificat médical délivré par un médecin
                                    psychiatrique
                                </span>
                            </Col>
                        </Row>
                    )}

                    {suiviTraitementPsychiatrique === false && (
                        <Row gutters>
                            <Col lg={12}>
                                <div className="margin-bottom-sm">
                                    <label
                                        htmlFor="jamaisEteEnTraitementPsychiatrique"
                                        className="fr-label fr-pb-2w"
                                    >
                                        {isMineurNonEmancipe
                                            ? "Je déclare sur l'honneur que le détenteur mineur n'a jamais été en traitement dans un hôpital psychiatrique *"
                                            : "Je déclare sur l'honneur n'avoir jamais été en traitement dans un hôpital psychiatrique *"}
                                    </label>
                                    <CheckboxControl
                                        id="jamaisEteEnTraitementPsychiatrique"
                                        name="jamaisEteEnTraitementPsychiatrique"
                                        {...register('jamaisEteEnTraitementPsychiatrique', {
                                            required: 'Ce champ est obligatoire',
                                        })}
                                        state={
                                            formState.errors?.jamaisEteEnTraitementPsychiatrique
                                                ? 'error'
                                                : null
                                        }
                                        message={
                                            formState.errors?.jamaisEteEnTraitementPsychiatrique
                                                ?.message
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                    <Row gutters className="margin-top-sm">
                        <Col lg={12}>
                            <label
                                htmlFor="installationConservationArmesCategoriesAEtB"
                                className="fr-label"
                            >
                                {isMineurNonEmancipe
                                    ? 'Le détenteur mineur est-il un compétiteur international ?'
                                    : 'Êtes-vous compétiteur primo-demandeur ?'}
                                {isMineurNonEmancipe ? (
                                    <span className="fr-hint-text">
                                        Uniquement pour les compétiteurs internationaux, vous pouvez
                                        ajouter une attestation fédérale justifiant du statut de
                                        compétiteur international du détenteur mineur (préalablement
                                        délivrée par la FFTIR) afin de débloquer son quota de 3 à 15
                                        armes pour la durée de son autorisation.
                                    </span>
                                ) : (
                                    <span className="fr-hint-text">
                                        Uniquement pour une première demande d’autorisation, vous
                                        pouvez ajouter une attestation fédérale justifiant votre
                                        statut de compétiteur (préalablement fournie par la FFTir)
                                        afin de débloquer votre quota de 6 à 15 armes pour la durée
                                        de votre autorisation
                                    </span>
                                )}
                            </label>
                            <FieldsetControl id="informationCompetiteur" inline>
                                <RadioControl
                                    className="margin-right-sm"
                                    id="informationCompetiteur-false"
                                    name="informationCompetiteur"
                                    onClick={() => setInformationCompetiteur(false)}
                                    label="Non"
                                    value={false}
                                    {...register('informationCompetiteur', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                />
                                <RadioControl
                                    className="margin-right-sm"
                                    id="informationCompetiteur-true"
                                    name="informationCompetiteur"
                                    onClick={() => setInformationCompetiteur(true)}
                                    label="Oui"
                                    value
                                    {...register('informationCompetiteur', {
                                        required: 'Ce champ est obligatoire',
                                    })}
                                />
                            </FieldsetControl>
                        </Col>
                    </Row>
                    {informationCompetiteur && (
                        <Row gutters className="margin-top-sm">
                            <Col lg={12}>
                                <InputFiles
                                    label={
                                        isMineurNonEmancipe
                                            ? 'Attestation fédérale justifiant le statut de compétiteur international'
                                            : 'Attestation de compétition FFTir'
                                    }
                                    {...register('pieceJointes.attestationCompetiteur.fichiers', {
                                        required: 'Ce champ est obligatoire',
                                        shouldUnregister: true,
                                    })}
                                    error={
                                        formState.errors
                                            ? formState.errors?.pieceJointes?.attestationCompetiteur
                                                  ?.fichiers
                                            : null
                                    }
                                />
                            </Col>
                        </Row>
                    )}
                    <Row gutters className="margin-top-md">
                        <Col lg={6}>
                            <Button
                                name="etapePrecedente"
                                variant="secondary"
                                onClick={() => dispatch(etapePrecedente())}
                            >
                                Retour
                            </Button>
                        </Col>
                        <Col lg={6}>
                            <div className="text-right">
                                <Button
                                    onClick={unregisterForm}
                                    name="deposer-demande"
                                    variant="primary"
                                    type="submit"
                                    isLoading={loading}
                                    disabled={
                                        !formState.isValid ||
                                        informationCompetiteur === undefined ||
                                        loading
                                    }
                                >
                                    Déposer la demande
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </form>
            </FormProvider>
        </>
    );
};

AutorisationDetentionArmeForm.propTypes = {
    defaultValues: PropTypes.object,
    loading: PropTypes.bool,
    enregistrer: PropTypes.func,
    isMineurNonEmancipe: PropTypes.bool,
};
export default AutorisationDetentionArmeForm;
